/**
 * Unify Admin Clients UI: Medplum patients + mock billing contacts, filters, and table.
 */

import {
  Badge,
  Container,
  Drawer,
  Group,
  Paper,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { IconCalendarEvent, IconSearch, IconUser, IconUsers, IconUsersGroup } from '@tabler/icons-react';
import type { Patient, RelatedPerson } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loading } from '../../../components/Loading';
import { formatDate } from '../../caseload/data';
import { dateInputValueToDateOnly, parseDateOnlyLocal } from '@/lib/dateHelpers';
import { TablePaginationFooter } from '@/components/TablePaginationFooter';
import { filterAdminClientsListRows } from './filterListRows';
import { mapMedplumPatientToListRow } from './mapMedplumPatient';
import type {
  AdminClientsListFilters,
  AdminClientsListRow,
  AdminClientsListSort,
  AdminClientsListTab,
  TriState,
} from './types';

const triStateData: { value: TriState; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const initialFilters: AdminClientsListFilters = {
  tab: 'all',
  search: '',
  guarantor: 'any',
  subscriber: 'any',
  dateOfBirth: '',
};

const initialSort: AdminClientsListSort = { column: 'name', direction: 'asc' };

function sortIndicator(active: boolean, dir: 'asc' | 'desc'): string {
  if (!active) return '';
  return dir === 'asc' ? ' ↑' : ' ↓';
}

function YesNoBadge(value: boolean): JSX.Element {
  return (
    <Badge size="sm" variant="light" color={value ? 'green' : 'gray'}>
      {value ? 'Yes' : 'No'}
    </Badge>
  );
}

function mapRelatedPersonToListRow(rp: RelatedPerson): AdminClientsListRow | null {
  if (!rp.id) return null;
  const name = rp.name?.[0];
  const firstName = name?.given?.join(' ') || '—';
  const lastName = name?.family?.trim() || '—';
  const displayName = [firstName, lastName].filter((s) => s !== '—').join(' ') || 'Unknown';

  return {
    id: rp.id,
    recordType: 'contact',
    displayName,
    legalFirstName: firstName,
    legalLastName: lastName,
    dateOfBirth: undefined,
    isGuarantor: false,
    isSubscriber: false,
    email: rp.telecom?.find((t) => t.system === 'email')?.value,
    phone: rp.telecom?.find((t) => t.system === 'phone')?.value,
    sourceLabel: 'Contact',
  };
}

export function UnifyAdminClientsPanel(): JSX.Element {
  const medplum = useMedplum();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [relatedPersons, setRelatedPersons] = useState<RelatedPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<AdminClientsListFilters>(initialFilters);
  const [sort, setSort] = useState<AdminClientsListSort>(initialSort);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState('50');
  const [contactDrawerRow, setContactDrawerRow] = useState<AdminClientsListRow | null>(null);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      medplum.searchResources('Patient', { _count: '200', _sort: '-_lastUpdated' }),
      medplum.searchResources('RelatedPerson', { _count: '200', _sort: '-_lastUpdated' }),
    ])
      .then(([pts, rps]) => {
        setPatients(pts);
        setRelatedPersons(rps);
      })
      .catch(() => {
        // Handled by empty state UI
      })
      .finally(() => setLoading(false));
  }, [medplum]);

  const allRows = useMemo(() => {
    const fromPatients = patients
      .map(mapMedplumPatientToListRow)
      .filter((r): r is AdminClientsListRow => r !== null);
    const fromRelated = relatedPersons
      .filter((rp) => rp.active !== false)
      .map(mapRelatedPersonToListRow)
      .filter((r): r is AdminClientsListRow => r !== null);
    return [...fromPatients, ...fromRelated];
  }, [patients, relatedPersons]);

  const filteredRows = useMemo(
    () => filterAdminClientsListRows(allRows, filters, sort),
    [allRows, filters, sort]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.tab, filters.search, filters.guarantor, filters.subscriber, filters.dateOfBirth]);

  const itemsPerPage = parseInt(rowsPerPage || '50', 10);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const pageSlice = filteredRows.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage);

  const setTab = (tab: AdminClientsListTab) => {
    setFilters((f) => ({ ...f, tab }));
    setCurrentPage(1);
  };

  const toggleSort = (column: AdminClientsListSort['column']) => {
    setSort((s) =>
      s.column === column
        ? { column, direction: s.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' }
    );
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>Clients</Title>
          <Text c="dimmed" size="sm">
            Billing and insurance directory: search clients and related contacts (guardians, subscribers).
            Filters apply on this page; the list loads from the server (up to 200 patients and related persons).
          </Text>
        </div>

        <Tabs value={filters.tab} onChange={(v) => v && setTab(v as AdminClientsListTab)}>
          <Tabs.List styles={{ list: { borderBottom: 'none' } }}>
            <Tabs.Tab value="all" leftSection={<IconUsers size={16} />} style={{ fontWeight: 400 }}>
              All
            </Tabs.Tab>
            <Tabs.Tab value="clients" leftSection={<IconUser size={16} />} style={{ fontWeight: 400 }}>
              Clients
            </Tabs.Tab>
            <Tabs.Tab value="contacts" leftSection={<IconUsersGroup size={16} />} style={{ fontWeight: 400 }}>
              Contacts
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>

        <Stack gap="sm">
          <Group gap="md" wrap="wrap" align="flex-end">
            <TextInput
              label="Search"
              placeholder="Name, email, phone…"
              leftSection={<IconSearch size={16} />}
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.currentTarget.value }))}
              style={{ flex: 1, minWidth: 220 }}
            />
            <Select
              label="Guarantor"
              data={triStateData}
              value={filters.guarantor}
              onChange={(v) => setFilters((f) => ({ ...f, guarantor: (v as TriState) ?? 'any' }))}
              w={130}
            />
            <Select
              label="Subscriber"
              data={triStateData}
              value={filters.subscriber}
              onChange={(v) => setFilters((f) => ({ ...f, subscriber: (v as TriState) ?? 'any' }))}
              w={130}
            />
            <DateInput
              label="Date of birth"
              placeholder="Select date"
              value={filters.dateOfBirth ? parseDateOnlyLocal(filters.dateOfBirth) : null}
              onChange={(v) =>
                setFilters((f) => ({ ...f, dateOfBirth: dateInputValueToDateOnly(v) }))
              }
              leftSection={<IconCalendarEvent size={16} />}
              clearable
              style={{ minWidth: 200 }}
            />
          </Group>

          <Paper p="lg" withBorder>
            {loading ? (
              <Loading fullScreen={false} />
            ) : pageSlice.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No records match your search and filters.
              </Text>
            ) : (
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('name')}>
                      Name{sortIndicator(sort.column === 'name', sort.direction)}
                    </Table.Th>
                    <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('type')}>
                      Type{sortIndicator(sort.column === 'type', sort.direction)}
                    </Table.Th>
                    <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('dob')}>
                      DOB{sortIndicator(sort.column === 'dob', sort.direction)}
                    </Table.Th>
                    <Table.Th>Guarantor</Table.Th>
                    <Table.Th>Subscriber</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Phone</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pageSlice.map((row) => (
                    <Table.Tr key={`${row.recordType}-${row.id}`}>
                      <Table.Td>
                        <Text size="sm" fw={500} lineClamp={2}>
                          {row.displayName}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light" color={row.recordType === 'client' ? 'blue' : 'grape'}>
                          {row.recordType === 'client' ? 'Client' : 'Contact'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={row.dateOfBirth ? undefined : 'dimmed'}>
                          {row.dateOfBirth ? formatDate(row.dateOfBirth) : '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>{YesNoBadge(row.isGuarantor)}</Table.Td>
                      <Table.Td>{YesNoBadge(row.isSubscriber)}</Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={2} c={row.email ? undefined : 'dimmed'}>
                          {row.email ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={2} c={row.phone ? undefined : 'dimmed'}>
                          {row.phone ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {row.recordType === 'client' && row.clientChartUrl ? (
                          <UnstyledButton
                            component={Link}
                            to={row.clientChartUrl}
                            fz="sm"
                            fw={500}
                            c="gold.6"
                            p={0}
                            styles={{ root: { textDecoration: 'none', textAlign: 'left' } }}
                          >
                            View chart
                          </UnstyledButton>
                        ) : (
                          <UnstyledButton
                            fz="sm"
                            fw={500}
                            c="gold.6"
                            p={0}
                            styles={{ root: { textAlign: 'left' } }}
                            onClick={() => {
                              setContactDrawerRow(row);
                              openDrawer();
                            }}
                          >
                            View contact
                          </UnstyledButton>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}

            {!loading && (
              <TablePaginationFooter
                totalItems={filteredRows.length}
                currentPage={safePage}
                onPageChange={setCurrentPage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(v) => setRowsPerPage(v ?? '50')}
              />
            )}
          </Paper>
        </Stack>
      </Stack>

      <Drawer
        opened={drawerOpened}
        onClose={() => {
          closeDrawer();
          setContactDrawerRow(null);
        }}
        title="Contact profile (limited)"
        position="right"
        size="md"
      >
        {contactDrawerRow && (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              This person does not have a full client chart in Unify. Use this view for billing, eligibility, and
              insurance workflows only.
            </Text>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Display name
              </Text>
              <Text size="md" fw={500}>
                {contactDrawerRow.displayName}
              </Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Legal name
              </Text>
              <Text size="sm">
                {contactDrawerRow.legalFirstName} {contactDrawerRow.legalLastName}
              </Text>
            </div>
            <Group gap="md">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Guarantor
                </Text>
                {YesNoBadge(contactDrawerRow.isGuarantor)}
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Subscriber
                </Text>
                {YesNoBadge(contactDrawerRow.isSubscriber)}
              </div>
            </Group>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Date of birth
              </Text>
              <Text size="sm">{contactDrawerRow.dateOfBirth ? formatDate(contactDrawerRow.dateOfBirth) : '—'}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Email
              </Text>
              <Text size="sm">{contactDrawerRow.email ?? '—'}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Phone
              </Text>
              <Text size="sm">{contactDrawerRow.phone ?? '—'}</Text>
            </div>
            <Text size="xs" c="dimmed">
              Reference ID for future RelatedPerson / Coverage APIs:{' '}
              <Text span ff="monospace">
                {contactDrawerRow.id}
              </Text>
            </Text>
          </Stack>
        )}
      </Drawer>
    </Container>
  );
}
